import { useState, useEffect, useMemo } from 'react';
import { Grid, Typography, IconButton, ListItemButton, Skeleton } from '@mui/material';
import { WorkOutlineRounded, EditRounded } from '@mui/icons-material';
import { ColorPicker } from '../../../../../Global/Components';
import { grey, blue, red, amber } from '@mui/material/colors';
import TaskBoxOptions from '../TaskBoxOptions';
import StateManager from '../../../../../Global/StateManager';
import useElementSize from '../../../../../Global/hooks/useElementSize';
import TasksTableDialog from './TasksTableDialog';
import { v4 } from 'uuid';
import axios from 'axios';
import MenuList from '../MenuList';
import TasksTable from './TasksTable';

const DueOptions = {
  today: { text: 'Due today', color: amber[600] },
  thisWeek: { text: 'Due this week', color: blue[600] },
  overdue: { text: 'Overdue', color: red[600] },
};

export default function TaskBox({ editable, onDelete, onEdit, params, onChange, itemId }) {
  const [optionsDialog, setOptionsDialog] = useState(false);
  const [boxParams, setBoxParams] = useState(null);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [tableDialogOpen, setTableDialogOpen] = useState(false);
  const [colorPicker, setColorPicker] = useState(null);
  const [color, setColor] = useState(undefined);
  const [elementId] = useState(v4());
  const { height, width } = useElementSize(elementId);
  const fontSize = Math.min(height / 2.5, width / 6);

  useEffect(() => {
    if (!params?.type) return;
    setBoxParams(params);
    setColor(params.color || undefined);
    if (editable) return;
    // if tile - load only tasks count, otherwise the table will load itself
    if (params.displayAs === 'tile') {
      setLoading(true);
      axios
        .post('/tasks/data/getTaskboxCount', { box: params })
        .then(({ data }) => {
          setCount(data.count);
        })
        .catch((err) => {
          StateManager.setAxiosErrorAlert(err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [params, editable]);

  const title = useMemo(() => {
    const suffix = boxParams && DueOptions[boxParams?.dueFilter] ? `(${DueOptions[boxParams?.dueFilter].text})` : '';
    if (!boxParams?.type) return;
    if (boxParams?.type === 'my') return `My tasks  ${suffix}`;
    if (boxParams?.type === 'assigned') return `Assigned tasks ${suffix}`;
    if (boxParams?.type === 'projects') return `Tasks from all projects ${suffix}`;
    if (boxParams?.type === 'project') return `Tasks from project '${boxParams.title}' ${suffix}`;
    if (boxParams?.type === 'process') return `Tasks from process '${boxParams.title}' ${suffix}`;
    if (boxParams?.type === 'team') return `Tasks from team '${boxParams.title}' ${suffix}`;
    return '';
  }, [boxParams]);

  function saveColor(col) {
    setColor(col);
    onChange({ color: col });
  }

  if (!boxParams?.type) return;

  return (
    <Grid container style={{ height: '100%', backgroundColor: color }} direction="column" id={elementId}>
      {editable ? (
        <>
          <ColorPicker
            anchor={colorPicker}
            open={Boolean(colorPicker)}
            column={400}
            customPicker
            onClose={(res) => {
              if (res?.color) {
                saveColor(res?.color);
              }
              setColorPicker(null);
            }}
            className="non-draggable"
          />
          {boxParams?.displayAs === 'table' && (
            <Grid container item alignItems="center" wrap="nowrap">
              <WorkOutlineRounded style={{ color: grey[500], marginRight: 16 }} />
              <Typography noWrap>Task box</Typography>
              <IconButton style={{ marginLeft: 'auto' }} onClick={(e) => setOptionsDialog(true)}>
                <EditRounded style={{ color: grey[500] }} />
              </IconButton>
              <MenuList
                onDelete={onDelete}
                color={color}
                onChange={onChange}
                setColorPicker={setColorPicker}
                mode={'taskBox'}
              />
            </Grid>
          )}

          <Grid container item alignItems="center" justifyContent="center" style={{ flex: 1 }} wrap="nowrap">
            <Typography color="textSecondary">{title}</Typography>
            {boxParams?.displayAs === 'tile' && (
              <>
                <IconButton style={{ marginLeft: 'auto' }} onClick={(e) => setOptionsDialog(true)}>
                  <EditRounded style={{ color: grey[500] }} />
                </IconButton>
                <MenuList
                  onDelete={onDelete}
                  color={color}
                  onChange={onChange}
                  setColorPicker={setColorPicker}
                  mode={'taskBox'}
                />
              </>
            )}
          </Grid>

          {editable && (
            <Grid className="non-draggable">
              <TaskBoxOptions
                open={optionsDialog}
                onClose={() => setOptionsDialog(false)}
                initial={boxParams}
                onResult={(res) => {
                  setBoxParams(res);
                  onEdit(res);
                }}
              />
            </Grid>
          )}
        </>
      ) : (
        <>
          {boxParams?.displayAs === 'tile' ? (
            loading ? (
              <Grid container alignItems="center" sx={{ height: '100%', width: '100%', p: 2 }}>
                <Skeleton variant="text" style={{ width: '70%' }} />
              </Grid>
            ) : (
              <>
                <ListItemButton
                  disabled={count === 0}
                  onClick={() => setTableDialogOpen(true)}
                  style={{ height: '100%', width: '100%', borderRadius: 5 }}
                >
                  <Grid container alignItems="center" wrap="nowrap" justifyContent="space-between">
                    <Typography variant="h6" fontSize={fontSize ?? 16}>
                      {title}
                    </Typography>
                    <Typography
                      style={{
                        fontSize: fontSize * 1.3,
                        fontWeight: 600,
                        marginLeft: 8,
                      }}
                    >
                      {count ?? 0}
                    </Typography>
                  </Grid>
                </ListItemButton>
                <TasksTableDialog
                  boxParams={boxParams}
                  title={title}
                  open={tableDialogOpen}
                  onClose={() => setTableDialogOpen(false)}
                  itemId={itemId}
                />
              </>
            )
          ) : (
            <Grid container item direction={'column'} wrap="nowrap" sx={{ height: '100%', p: 1 }}>
              <Grid container item alignItems="center" sx={{ pb: 0.5 }}>
                <WorkOutlineRounded style={{ color: grey[500], marginRight: 16 }} />
                <Typography variant="h6">{title}</Typography>
              </Grid>
              <Grid container item sx={{ flex: 1, position: 'relative' }}>
                <Grid container item sx={{ position: 'absolute', height: '100%' }}>
                  <TasksTable boxParams={boxParams} columnHeaderHeight={52} rowHeight={48} itemId={itemId} />
                </Grid>
              </Grid>
            </Grid>
          )}
        </>
      )}
    </Grid>
  );
}
